import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { Input, Select } from '@st-operations/uikit/src/v2'

import ValidatedForm from '../validated-form/index.jsx'
import { useEventFeedsContext } from '../../../context/index.jsx'

const CSV_TSV = ['csv', 'tsv']

const DONT_ALLOW_CSV_TSV = [
  'metadata',
  'analyzed text',
  'MAIDs',
  'HEMs',
  'keywords',
  'entities',
  'categories',
  'concepts',
]

const body = document.querySelector('body')

const FileConfigurationForm = ({
  requiredErrorFileFormat,
  requiredErrorFileCompression,
  requiredErrorFilePartitionBy,
}) => {
  const {
    data: { deliveries, attributes, type },
    schema,
    dispatch,
    ACTIONS,
  } = useEventFeedsContext()
  const isUup = useMemo(() => type === 'uup', [type])

  const deliveryFormat = useMemo(
    () =>
      isUup
        ? schema?.unified_user_profile?.deliveries?.format
        : schema?.deliveries?.['File Configuration']?.format,
    [isUup, schema]
  )
  const delivery = useMemo(() => deliveries?.[0] ?? {}, [deliveries])

  const hasAttribute = useMemo(() => DONT_ALLOW_CSV_TSV.some((item) => attributes.includes(item)), [
    attributes,
  ])

  const formatOptions = useMemo(() => {
    const enumContent = deliveryFormat?.enum
    return !isUup && hasAttribute
      ? enumContent.filter((item) => !CSV_TSV.includes(item))
      : enumContent
  }, [isUup, deliveryFormat, hasAttribute])

  const onDeliveryChange = (option, name, value) => {
    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        [name]: value,
      },
    })
  }

  const onNumFilesChange = (value) => {
    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        numFiles: parseInt(value, 10),
      },
    })
  }

  // Make sure we add num_files to the delivery if it's not there
  useEffect(() => {
    if (delivery?.numFiles !== null && delivery?.numFiles !== undefined) return

    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        numFiles: 100,
      },
    })
  }, [delivery.numFiles])

  return (
    <ValidatedForm
      inputs={[
        {
          label: 'File Format',
          required: requiredErrorFileFormat,
          validationClassName: 'delivery-content__required--file-configuration',
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--file-configuration'
                )}
              >
                File Format
              </span>
              <Select
                name="format"
                value={delivery.format}
                onChange={onDeliveryChange}
                options={formatOptions}
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorFileFormat,
                })}
                pretty
                isSearchable
                menuPortalTarget={body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    maxHeight: 200,
                  }),
                }}
                useReactSelect
              />
            </>
          ),
        },
        {
          label: 'File Compression',
          required: requiredErrorFileCompression,
          validationClassName: 'delivery-content__required--file-configuration',
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--file-configuration'
                )}
              >
                File Compression
              </span>
              <Select
                name="compression"
                value={delivery.compression}
                onChange={onDeliveryChange}
                options={schema?.deliveries?.['File Configuration']?.compression?.enum}
                pretty
                className={classNames('delivery-content__content-select', {
                  'delivery-content__required-field': requiredErrorFileCompression,
                })}
                isSearchable
                menuPortalTarget={body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    maxHeight: 200,
                  }),
                }}
                useReactSelect
              />
            </>
          ),
        },
        !isUup
          ? {
              label: 'Partition by',
              required: requiredErrorFilePartitionBy,
              validationClassName: 'delivery-content__required--file-configuration',
              render: () => (
                <>
                  <span
                    className={classNames(
                      'delivery-content__content-label',
                      'delivery-content__content-label--file-configuration'
                    )}
                  >
                    Partition by
                  </span>
                  <Select
                    name="partitionType"
                    value={delivery.partitionType}
                    onChange={onDeliveryChange}
                    options={schema?.deliveries?.['File Configuration']?.partition_type?.enum}
                    pretty
                    className={classNames('delivery-content__content-select', {
                      'delivery-content__required-field': requiredErrorFilePartitionBy,
                    })}
                    isSearchable
                    menuPortalTarget={body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                        maxHeight: 200,
                      }),
                    }}
                    useReactSelect
                  />
                </>
              ),
            }
          : null,
        {
          label: 'Number of files',
          validationClassName: 'delivery-content__required--file-configuration',
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-content__content-label',
                  'delivery-content__content-label--file-configuration'
                )}
              >
                Number of files
              </span>
              <Input
                type="number"
                name="numFiles"
                min="1"
                max="1000"
                required
                className="delivery-content__content-select"
                value={delivery.numFiles}
                onChange={onNumFilesChange}
              />
            </>
          ),
        },
      ].filter(Boolean)}
    />
  )
}

export default FileConfigurationForm
